<template>
  <!-- 透传所有属性和事件 -->
  <el-select
    v-bind="$attrs"
    :model-value="internalValue"
    :loading="loading"
    clearable
    :filterable="props.filterable"
    :allow-create="props.allowCreate"
    :multiple="props.multiple"
    :disabled="disabled"
    :remote-method="loadOptions"
    @update:modelValue="handleUpdateModelValue"
  >
    <!-- 渲染默认插槽（允许自定义option） -->
    <slot v-if="$slots.default" />
    <!-- 默认渲染options -->
    <template v-else>
      <el-option
        v-for="item in options"
        :key="item.value"
        :label="item.label"
        :value="item.value"
        v-bind="item"
      />
    </template>
  </el-select>
</template>

<script setup>
import { ref, watchEffect, watch, nextTick  } from "vue";

const props = defineProps({
  // 必须：返回Promise的函数
  promiseFn: {
    type: Function,
    required: true,
  },
  // 可选：监听参数变化重新请求
  params: {
    type: [Object, Array, String, Number],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // 可选：立即执行（默认true）
  immediate: {
    type: Boolean,
    default: true,
  },
  filterable:{
    type: Boolean,
    default: true,
  },
  allowCreate:{
    type: Boolean,
    default: false,
  },
  multiple:{
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [String, Array],
    default: undefined,
  },
});

const emit = defineEmits(["update:modelValue", "options-loaded"]);

const options = ref([]);
const loading = ref(false);
const error = ref(null);
// 内部状态管理：处理多选时的逗号分隔字符串
const internalValue = ref(props.multiple ? [] : "");

// 初始化内部值
const initializeValue = () => {
  if (props.modelValue === undefined) {
    // 初始值未定义时，根据多选/单选设置默认值
    internalValue.value = props.multiple ? [] : "";
  } else if (props.multiple) {
    // 多选时，将外部传入的值转换为数组
    if (typeof props.modelValue === "string") {
      internalValue.value = props.modelValue ? props.modelValue.split(",") : [];
    } else {
      internalValue.value = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    }
  } else {
    // 单选时，直接使用外部传入的值
    internalValue.value = props.modelValue || "";
  }
};

// 初始化
initializeValue();


// 同步外部传入的modelValue到内部值
watch(() => props.modelValue, (newValue) => {
  if (props.multiple) {
    // 多选时，将外部传入的字符串转换为数组
    if (typeof newValue === "string") {
      internalValue.value = newValue ? newValue.split(",") : [];
    } else if (Array.isArray(newValue)) {
      internalValue.value = [...newValue];
    } else if (newValue === null || newValue === undefined) {
      internalValue.value = [];
    }
  } else {
    // 单选时，直接使用外部传入的值
    internalValue.value = newValue || "";
  }
}, { immediate: true, deep: true });

// 核心数据加载逻辑
const loadOptions = async (query = "") => {
  loading.value = true;
  error.value = null;
  try {
    const data = await props.promiseFn(query, props.params);
    options.value = Array.isArray(data) ? data : [];
    emit("options-loaded", options.value);
  } catch (err) {
    error.value = err;
    console.error("AsyncSelect加载失败:", err);
    options.value = [];
  } finally {
    loading.value = false;
  }
};

// 处理值更新，根据单选/多选返回不同格式的字符串
const handleUpdateModelValue = (value) => {
  internalValue.value = value;
  if (props.multiple) {
    // 多选模式：将选中的值数组转换为用逗号分隔的字符串
    emit("update:modelValue", (value || []).join(","));
  } else {
    // 单选模式：直接输出值
    emit("update:modelValue", value);
  }
};

// 自动监听params变化
watchEffect(() => {
  if (props.immediate) {
    loadOptions();
  }
});

// 暴露方法给父组件
defineExpose({
  reload: loadOptions,
  options,
  loading,
  error,
  // 表单重置时会调用此方法
  resetField: () => {
    // 重置内部值
    initializeValue();
    // 通知父组件值已重置
    emit("update:modelValue", props.multiple ? "" : "");
  },
});
</script>